import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Avatar,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  Grid,
  Stack,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useTheme } from "@mui/material/styles";

const MyAccount = () => {
  const theme = useTheme();
  const user = useSelector((state) => state.auth.user);
  // Password form state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  // Placeholder for photo upload
  const handlePhotoUpload = () => {
    // Implement photo upload logic here
    alert("Photo upload not implemented");
  };

  // Placeholder for password update
  const handlePasswordUpdate = (e) => {
    e.preventDefault();
    if (!currentPassword) {
      setPasswordError("Current password is required.");
      return;
    }
    if (!newPassword) {
      setPasswordError("New password is required.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }
    setPasswordError("");
    alert("Password update not implemented");
  };

  return (
    <Box sx={{ width: "100%", height: "100%", overflow: "hidden", backgroundColor: theme.palette.background.paper }}>
    <Box sx={{ p: { xs: 1, md: 4 }, maxWidth: 1100, mx: "auto" }}>
      <Typography variant="h5" fontWeight={600} mb={3} sx={{ color: theme.palette.text.primary }}>
        Profile
      </Typography>
      <Grid container spacing={3}>
        {/* Profile Info */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Profile Information
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Update your account's profile information and email address.
            </Typography>
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: "primary.main" }}>
                {user?.fullName?.[0] || <AccountCircleIcon fontSize="large" />}
              </Avatar>
              <Button variant="contained" onClick={handlePhotoUpload}>
                SELECT A NEW PHOTO
              </Button>
            </Stack>
            <TextField
              label="Name"
              value={user?.fullName || ""}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Email"
              value={user?.email || ""}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
            />
            <Box textAlign="right" mt={2}>
              <Button variant="contained" disabled>
                SAVE
              </Button>
            </Box>
          </Paper>
        </Grid>
        {/* Password Update */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Update Password
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Ensure your account is using a long, random password to stay secure.
            </Typography>
            <form onSubmit={handlePasswordUpdate}>
              <TextField
                label="Current Password"
                type="password"
                value={currentPassword}
                onChange={e => setCurrentPassword(e.target.value)}
                fullWidth
                margin="normal"
                error={!!passwordError && !currentPassword}
                helperText={!!passwordError && !currentPassword ? passwordError : ""}
              />
              <TextField
                label="New Password"
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                fullWidth
                margin="normal"
                error={!!passwordError && !newPassword}
                helperText={!!passwordError && !newPassword ? passwordError : ""}
              />
              <TextField
                label="Confirm Password"
                type="password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                fullWidth
                margin="normal"
                error={!!passwordError && newPassword !== confirmPassword}
                helperText={!!passwordError && newPassword !== confirmPassword ? passwordError : ""}
              />
              <Box textAlign="right" mt={2}>
                <Button variant="contained" type="submit">
                  SAVE
                </Button>
              </Box>
            </form>
          </Paper>
        </Grid>
        {/* 2FA and Sessions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Two Factor Authentication
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add additional security to your account using two factor authentication.
            </Typography>
            <Button variant="outlined" fullWidth sx={{ mt: 1 }} disabled>
              TWO FACTOR AUTHENTICATION
            </Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              Browser Sessions
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Manage and log out your active sessions on other browsers and devices.
            </Typography>
            <Button variant="outlined" fullWidth sx={{ mt: 1 }} disabled>
              LOG OUT OTHER BROWSER SESSIONS
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box>  
    </Box>
  );
};

export default MyAccount; 